/**
 * Admin — Audit Log
 *
 * Queryable, paginated view of the immutable security audit trail.
 * Consumes:
 *   GET /api/admin/audit-log       — paginated, filterable entries
 *   GET /api/admin/audit-log/event-types — distinct event types for dropdown
 *
 * English only — system admin UI (per CLAUDE.md).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../api/client';
import { fetchAllSecurityUsers, type SecurityUserResponse } from '../../api/security';
import styles from './AdminPages.module.css';

// ── Types ──────────────────────────────────────────────────────

interface AuditEntry {
  id: number;
  eventType: string;
  userGuid: string;
  actorGuid: string | null;
  ipAddress: string | null;
  details: string | null;
  createdAt: string;
}

interface AuditPage {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  entries: AuditEntry[];
}

// ── Helpers ────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatEventLabel(raw: string): string {
  return raw
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

function eventTypeColor(raw: string): string {
  const lower = raw.toLowerCase();
  if (lower.includes('login') || lower.includes('auth') || lower.includes('session')) return '#2563eb';
  if (lower.includes('create') || lower.includes('add') || lower.includes('invite') || lower.includes('register')) return '#16a34a';
  if (lower.includes('delete') || lower.includes('remove') || lower.includes('suspend') || lower.includes('block')) return '#dc2626';
  if (lower.includes('update') || lower.includes('edit') || lower.includes('change') || lower.includes('modify')) return '#d97706';
  if (lower.includes('grant') || lower.includes('permission') || lower.includes('role')) return '#7c3aed';
  return '#6b7280';
}

// ── Inline styles (admin-only surface) ─────────────────────────

const S = {
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 12,
    marginBottom: 20,
    alignItems: 'flex-end',
  } as React.CSSProperties,
  filterGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 3,
  } as React.CSSProperties,
  filterLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  } as React.CSSProperties,
  select: {
    padding: '7px 10px',
    fontSize: 13,
    color: '#1a1a2e',
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: 5,
    fontFamily: 'inherit',
    minWidth: 160,
  } as React.CSSProperties,
  dateInput: {
    padding: '7px 10px',
    fontSize: 13,
    color: '#1a1a2e',
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: 5,
    fontFamily: 'inherit',
    width: 150,
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: 13,
  } as React.CSSProperties,
  th: {
    textAlign: 'start' as const,
    padding: '10px 12px',
    fontSize: 11,
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    borderBottom: '2px solid #e5e7eb',
    background: '#fafafa',
  } as React.CSSProperties,
  td: {
    padding: '10px 12px',
    borderBottom: '1px solid #f3f4f6',
    color: '#374151',
    verticalAlign: 'top' as const,
  } as React.CSSProperties,
  badge: (color: string) => ({
    display: 'inline-block',
    padding: '2px 8px',
    fontSize: 11,
    fontWeight: 600,
    borderRadius: 4,
    background: `${color}14`,
    color,
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties),
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 0',
    fontSize: 13,
    color: '#6b7280',
  } as React.CSSProperties,
  pageBtn: (disabled: boolean) => ({
    padding: '6px 14px',
    fontSize: 12,
    fontWeight: 500,
    color: disabled ? '#c8ccd4' : '#374151',
    background: disabled ? '#fafafa' : '#ffffff',
    border: `1px solid ${disabled ? '#e5e7eb' : '#d1d5db'}`,
    borderRadius: 5,
    cursor: disabled ? 'default' : 'pointer',
    transition: 'all 0.15s ease',
  } as React.CSSProperties),
  emptyRow: {
    padding: '40px 12px',
    textAlign: 'center' as const,
    color: '#9ca3af',
    fontSize: 14,
  } as React.CSSProperties,
  detailsCell: {
    maxWidth: 400,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    fontSize: 12,
    color: '#6b7280',
  } as React.CSSProperties,
  summaryBar: {
    display: 'flex',
    gap: 16,
    marginBottom: 16,
    fontSize: 12,
    color: '#6b7280',
  } as React.CSSProperties,
};

const PAGE_SIZE = 50;

// ── Component ──────────────────────────────────────────────────

export default function AdminAuditLog() {
  // ── Data state ──
  const [result, setResult] = useState<AuditPage | null>(null);
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [userMap, setUserMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Filter state ──
  const [filterType, setFilterType] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // ── Load event types + user lookup (once) ──
  useEffect(() => {
    (async () => {
      try {
        const [types, users] = await Promise.allSettled([
          apiFetch<string[]>('/api/admin/audit-log/event-types'),
          fetchAllSecurityUsers(),
        ]);
        if (types.status === 'fulfilled' && types.value) setEventTypes(types.value);
        if (users.status === 'fulfilled') {
          const map = new Map<string, string>();
          for (const u of users.value) {
            map.set(u.userGuid.toUpperCase(), u.displayName || u.name || u.email);
          }
          setUserMap(map);
        }
      } catch { /* non-critical */ }
    })();
  }, []);

  // ── Fetch log entries ──
  const fetchLog = useCallback(async (pg: number) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(pg), pageSize: String(PAGE_SIZE) });
      if (filterType) params.set('eventType', filterType);
      if (filterFrom) params.set('from', new Date(filterFrom).toISOString());
      if (filterTo) params.set('to', new Date(filterTo + 'T23:59:59').toISOString());

      const data = await apiFetch<AuditPage>(`/api/admin/audit-log?${params}`);
      if (mountedRef.current) setResult(data);
    } catch (err: any) {
      if (mountedRef.current) setError(err.message || 'Failed to load audit log');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [filterType, filterFrom, filterTo]);

  useEffect(() => { fetchLog(page); }, [fetchLog, page]);

  // ── Resolve user GUID → display name ──
  const resolveUser = (guid: string | null): string => {
    if (!guid) return '\u2014';
    const name = userMap.get(guid.toUpperCase());
    return name || guid.substring(0, 8) + '\u2026';
  };

  // ── CSV export ──
  const handleExportCsv = useCallback(async () => {
    setExporting(true);
    try {
      // Fetch all matching entries (up to 5000)
      const params = new URLSearchParams({ page: '1', pageSize: '200' });
      if (filterType) params.set('eventType', filterType);
      if (filterFrom) params.set('from', new Date(filterFrom).toISOString());
      if (filterTo) params.set('to', new Date(filterTo + 'T23:59:59').toISOString());

      let allEntries: AuditEntry[] = [];
      let currentPage = 1;
      let totalPages = 1;

      while (currentPage <= totalPages && currentPage <= 25) {
        params.set('page', String(currentPage));
        const batch = await apiFetch<AuditPage>(`/api/admin/audit-log?${params}`);
        if (!batch) break;
        allEntries = [...allEntries, ...batch.entries];
        totalPages = batch.totalPages;
        currentPage++;
      }

      // Build CSV
      const header = 'ID,Event Type,User,Actor,IP Address,Details,Timestamp';
      const rows = allEntries.map((e) =>
        [
          e.id,
          `"${e.eventType}"`,
          `"${resolveUser(e.userGuid)}"`,
          `"${resolveUser(e.actorGuid)}"`,
          `"${e.ipAddress || ''}"`,
          `"${(e.details || '').replace(/"/g, '""')}"`,
          `"${e.createdAt}"`,
        ].join(','),
      );
      const csv = [header, ...rows].join('\n');

      // Download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(`Export failed: ${err.message || 'Unknown error'}`);
    } finally {
      setExporting(false);
    }
  }, [filterType, filterFrom, filterTo, resolveUser]);

  // ── Filter handlers ──
  const handleApplyFilters = () => { setPage(1); };
  const handleClearFilters = () => {
    setFilterType('');
    setFilterFrom('');
    setFilterTo('');
    setPage(1);
  };
  const hasFilters = filterType || filterFrom || filterTo;

  return (
    <div>
      {/* ── Header ── */}
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Audit Log</h1>
          <p className={styles.pageSubtitle}>Immutable security audit trail &mdash; all admin and user actions</p>
        </div>
        <div className={styles.pageActions}>
          <button
            className={styles.btnSecondary}
            onClick={handleExportCsv}
            disabled={exporting || !result || result.totalCount === 0}
          >
            {exporting ? 'Exporting\u2026' : 'Export CSV'}
          </button>
        </div>
      </div>

      {error && (
        <div className={`${styles.statusBar} ${styles.status_error}`}>{error}</div>
      )}

      {/* ── Filters ── */}
      <div className={styles.section}>
        <div style={S.filterBar}>
          <div style={S.filterGroup}>
            <span style={S.filterLabel}>Event Type</span>
            <select
              style={S.select}
              value={filterType}
              onChange={(e) => { setFilterType(e.target.value); setPage(1); }}
            >
              <option value="">All Events</option>
              {eventTypes.map((t) => (
                <option key={t} value={t}>{formatEventLabel(t)}</option>
              ))}
            </select>
          </div>

          <div style={S.filterGroup}>
            <span style={S.filterLabel}>From</span>
            <input
              type="date"
              style={S.dateInput}
              value={filterFrom}
              onChange={(e) => { setFilterFrom(e.target.value); setPage(1); }}
            />
          </div>

          <div style={S.filterGroup}>
            <span style={S.filterLabel}>To</span>
            <input
              type="date"
              style={S.dateInput}
              value={filterTo}
              onChange={(e) => { setFilterTo(e.target.value); setPage(1); }}
            />
          </div>

          {hasFilters && (
            <button
              className={styles.btnSecondary}
              style={{ alignSelf: 'flex-end' }}
              onClick={handleClearFilters}
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* ── Summary ── */}
      {result && (
        <div style={S.summaryBar}>
          <span><strong>{result.totalCount.toLocaleString()}</strong> events</span>
          <span>Page {result.page} of {result.totalPages || 1}</span>
          {hasFilters && <span style={{ color: '#d97706' }}>Filtered</span>}
        </div>
      )}

      {/* ── Table ── */}
      <div className={styles.section} style={{ padding: 0, overflow: 'hidden' }}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Timestamp</th>
              <th style={S.th}>Event</th>
              <th style={S.th}>User</th>
              <th style={S.th}>Actor</th>
              <th style={S.th}>IP</th>
              <th style={S.th}>Details</th>
            </tr>
          </thead>
          <tbody>
            {loading && !result ? (
              <tr><td colSpan={6} style={S.emptyRow}>Loading audit log&hellip;</td></tr>
            ) : result && result.entries.length > 0 ? (
              result.entries.map((entry) => (
                <tr key={entry.id} style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.15s' }}>
                  <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: 12 }}>
                    <div>{formatDate(entry.createdAt)}</div>
                    <div style={{ color: '#9ca3af' }}>{formatTime(entry.createdAt)}</div>
                  </td>
                  <td style={S.td}>
                    <span style={S.badge(eventTypeColor(entry.eventType))}>
                      {formatEventLabel(entry.eventType)}
                    </span>
                  </td>
                  <td style={{ ...S.td, fontSize: 12 }} title={entry.userGuid}>
                    {resolveUser(entry.userGuid)}
                  </td>
                  <td style={{ ...S.td, fontSize: 12 }} title={entry.actorGuid || ''}>
                    {resolveUser(entry.actorGuid)}
                  </td>
                  <td style={{ ...S.td, fontSize: 12, fontFamily: 'monospace', color: '#6b7280' }}>
                    {entry.ipAddress || '\u2014'}
                  </td>
                  <td style={{ ...S.td, ...S.detailsCell }} title={entry.details || ''}>
                    {entry.details || '\u2014'}
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={6} style={S.emptyRow}>No audit entries found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {result && result.totalPages > 1 && (
        <div style={S.pagination}>
          <button
            style={S.pageBtn(page <= 1)}
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            &larr; Previous
          </button>
          <span>
            Page <strong>{result.page}</strong> of <strong>{result.totalPages}</strong>
            &nbsp;&middot;&nbsp;{result.totalCount.toLocaleString()} total
          </span>
          <button
            style={S.pageBtn(page >= result.totalPages)}
            disabled={page >= result.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next &rarr;
          </button>
        </div>
      )}
    </div>
  );
}
